import { create } from 'zustand';
import type { WeatherStation } from '../types/weatherStation';
import { createLogger } from '../utils/logger';
import { fetchJSON } from '../utils/fetchClient';
import { getStationTier } from '../utils/stationTier';

const log = createLogger('StationDataStore');

export interface StationDataState {
  allStations: WeatherStation[];
  isLoadingAllStations: boolean;
  selectedStation: WeatherStation | null;
  selectedStations: WeatherStation[];
}

export interface StationDataActions {
  loadAllStations: () => Promise<void>;
  setSelectedStation: (station: WeatherStation | null) => void;
  setSelectedStations: (stations: WeatherStation[]) => void;
}

export type StationDataStore = StationDataState & StationDataActions;

export const useStationDataStore = create<StationDataStore>((set, get) => ({
  allStations: [],
  isLoadingAllStations: false,
  selectedStation: null,
  selectedStations: [],

  setSelectedStation: (station) => set({ selectedStation: station }),

  setSelectedStations: (stations) => set({ selectedStations: stations }),

  loadAllStations: async () => {
    if (get().allStations.length > 0) {
      log.debug('Stations already loaded, skipping...');
      return;
    }

    log.debug('Starting to load all stations...');
    set({ isLoadingAllStations: true });

    try {
      const baseURL = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001';

      let stationsData: any[] = [];
      let source = 'R2';

      try {
        // ponytail: fields=map drops unused metadata fields server-side, shrinking payload ~30-50%
        const r2Data = await fetchJSON<{ success?: boolean; metadata?: any }>(`${baseURL}/api/station-metadata?fields=map`, {
          signal: AbortSignal.timeout(60000),
        });

        if (!r2Data?.success || !r2Data?.metadata) {
          throw new Error('station-metadata returned invalid response');
        }

        const raw = r2Data.metadata;
        stationsData = raw.stations && Array.isArray(raw.stations)
          ? raw.stations
          : Array.isArray(raw) ? raw : [];
      } catch (r2Error) {
        log.warn('⚠️ R2 metadata unavailable, falling back to weather API:', r2Error instanceof Error ? r2Error.message : r2Error);
        source = 'weather API';
        const fallbackData = await fetchJSON<any[]>(`${baseURL}/api/weather/weatherstations`, {
          signal: AbortSignal.timeout(180000),
        });
        if (!fallbackData || !Array.isArray(fallbackData)) {
          throw new Error('weatherstations returned non-array');
        }
        stationsData = fallbackData;
      }

      // Lightweight transform — handles both R2 (snake_case) and weather API (camelCase) formats
      const stations: WeatherStation[] = [];
      for (const s of stationsData) {
        const rawLat = s.geoPoint?.latitude ?? s.latitude;
        const rawLon = s.geoPoint?.longitude ?? s.longitude;
        const lat = typeof rawLat === 'number' ? rawLat : parseFloat(rawLat) || 0;
        const lon = typeof rawLon === 'number' ? rawLon : parseFloat(rawLon) || 0;
        if (lat === 0 && lon === 0) continue;
        if (Math.abs(lat) === 90) continue;

        const rawTags = s.tags;
        const tags = typeof rawTags === 'string' ? ((() => { try { return JSON.parse(rawTags); } catch { return {}; } })()) : (rawTags || {});

        let devices: Array<{ stationId: string; sourceStationId?: string; dataSourceId: string; type: string }> = [];
        const rawDevices = s.devices;
        if (typeof rawDevices === 'string') {
          try {
            const parsed = JSON.parse(rawDevices);
            if (Array.isArray(parsed)) {
              devices = parsed.map((d: any) => ({
                stationId: d.station_id || d.stationId || '',
                dataSourceId: d.data_source_id || d.dataSourceId || '',
                type: d.network_type || d.type || '',
              }));
            }
          } catch { /* ignore malformed devices JSON */ }
        } else if (Array.isArray(rawDevices)) {
          devices = rawDevices.map((d: any) => ({
            stationId: d.station_id || d.stationId || '',
            dataSourceId: d.data_source_id || d.dataSourceId || '',
            type: d.network_type || d.type || '',
          }));
        }

        stations.push({
          stationCode: s.station_code || s.stationCode || '',
          latitude: lat,
          longitude: lon,
          elevation: typeof s.elevation === 'number' ? s.elevation : parseFloat(s.elevation) || 0,
          country: s.country || '',
          state: s.state || '',
          tags,
          firstObsTimestamp: s.first_obs_timestamp || s.firstObsTimestamp || '',
          lastObsTimestamp: s.last_obs_timestamp || s.lastObsTimestamp || '',
          startTime: s.start_time || s.startTime,
          devices,
          isActive: s.active === true || s.active === 'true',
          isPrimaryResult: true,
          stationTier: getStationTier(devices),
          parameters: (() => {
            const raw = s.parameters;
            if (Array.isArray(raw)) return raw.filter(Boolean) as string[];
            if (typeof raw === 'string') {
              try { const parsed = JSON.parse(raw); return Array.isArray(parsed) ? parsed.filter(Boolean) : []; }
              catch { return []; }
            }
            return [];
          })(),
        });
      }

      log.info(`✅ Successfully loaded ${stations.length} stations from ${source}`);
      set({ allStations: stations, isLoadingAllStations: false });
    } catch (error) {
      log.error('❌ Failed to load all stations:', error);
      set({ isLoadingAllStations: false });
    }
  },
}));
